<script lang="ts" setup>
import {inject, ref, watch} from 'vue'

const props = withDefaults(defineProps<{
  value?: number | null
  id?: string
}>(), {
  value: null,
  id: undefined,
})

// const emit = defineEmits<{
//   input: [schoolId: number | null]
// }>()
const emit = defineEmits(['input']) // typed version of defineEmits doesn't work in 2.7

interface SchoolOption {
  schoolid: number
  schoolname: string
  schoolcity: string | null
  schoolstate: string | null
}

// The WithServices component on the admin layout provides 'service'
const service: any = inject('service')

const query = ref('')
const results = ref<SchoolOption[]>([])
const selectedId = ref<number | null>(props.value)
const busy = ref(false)

// Debounce search
let timer: ReturnType<typeof setTimeout> | null = null

function search() {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => doSearch(), 300)
}

async function doSearch() {
  const q = query.value.trim()
  if (!q) {
    results.value = []
    return
  }

  busy.value = true
  try {
    const schoolService = await service('school')
    let params: Record<string, string>
    if (/^\d+$/.test(q)) {
      params = { schoolid_eq: q }
    } else {
      params = { schoolname_like: `%${q}%` }
    }
    const response = await schoolService.browse(params)
    // Deduplicate by schoolid — the API may return the same school multiple times
    const seen = new Set<number>()
    results.value = (response.data.data.data || []).filter((s: SchoolOption) => {
      if (seen.has(s.schoolid)) return false
      seen.add(s.schoolid)
      return true
    })
  } catch {
    results.value = []
  } finally {
    busy.value = false
  }
}

function select(school: SchoolOption) {
  selectedId.value = school.schoolid
  query.value = school.schoolname
  results.value = []
  emit('input', school.schoolid)
}

function clear() {
  selectedId.value = null
  query.value = ''
  results.value = []
  emit('input', null)
}

function displayName(school: SchoolOption): string {
  const parts = [school.schoolname]
  if (school.schoolcity) parts.push(school.schoolcity)
  if (school.schoolstate) parts.push(school.schoolstate)
  return `${parts.join(', ')} — ID ${school.schoolid}`
}

// Sync external value changes
watch(() => props.value, (newVal) => {
  if (newVal === null) {
    selectedId.value = null
    query.value = ''
  }
})
</script>

<template>
  <div class="school-picker">
    <div class="input-group">
      <input
        :id="props.id"
        v-model="query"
        type="text"
        class="form-control"
        placeholder="Search by school name or ID..."
        @input="search"
        @focus="search"
      />
      <div v-if="query || selectedId" class="input-group-append">
        <button class="btn btn-outline-secondary" type="button" @click="clear">✕</button>
      </div>
    </div>

    <div v-if="results.length > 0" class="dropdown-results">
      <button
        v-for="school in results"
        :key="school.schoolid"
        class="dropdown-item"
        :class="{ active: school.schoolid === selectedId }"
        type="button"
        @click="select(school)"
      >
        {{ displayName(school) }}
      </button>
    </div>

    <div v-if="busy" class="text-muted small mt-1">Searching...</div>
    <div v-if="selectedId" class="text-success small mt-1">Selected: School ID {{ selectedId }}</div>
  </div>
</template>

<style lang="scss" scoped>
.school-picker {
  position: relative;
}

.dropdown-results {
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-height: 15rem;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dropdown-item {
  padding: 0.4rem 0.75rem;
  font-size: 0.875rem;
  cursor: pointer;
  border: none;
  width: 100%;
  text-align: left;
  background: none;
}

.dropdown-item:hover,
.dropdown-item.active {
  background-color: #e9ecef;
}
</style>
